import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PaziaFormButton, PaziaInput, PaziaSelect, PaziaSwitch } from "components/atoms";
import { KSeFEnvironment, KSeFIntegrationApi } from "api/ksef";

export interface KSeFIntegrationFormValues {
  name: string;
  environment: KSeFEnvironment;
  baseUrl: string;
  nip: string;
  authType: string;
  authToken?: string;
  isEnabled: boolean;
  autoSubmit: boolean;
  sellerName?: string;
  sellerAddress?: string;
  sellerNIP?: string;
  sellerBankAccount?: string;
}

interface KSeFIntegrationFormProps {
  onSubmit: (data: KSeFIntegrationFormValues) => void;
  data?: KSeFIntegrationApi;
}

const schema = yup.object({
  name: yup.string().required(),
  environment: yup.number().required(),
  baseUrl: yup.string().required(),
  nip: yup.string().required(),
  authType: yup.string().oneOf(["token", "xades"]).required(),
  authToken: yup.string().optional(),
  isEnabled: yup.boolean().required(),
  autoSubmit: yup.boolean().required(),
  sellerName: yup.string().optional(),
  sellerAddress: yup.string().optional(),
  sellerNIP: yup.string().optional(),
  sellerBankAccount: yup.string().optional(),
});

const environmentOptions = [
  { value: KSeFEnvironment.Test, label: "Test" },
  { value: KSeFEnvironment.Production, label: "Produkcja" },
];

const authTypeOptions = [
  { value: "token", label: "Token KSeF" },
  { value: "xades", label: "Podpis kwalifikowany (XAdES)" },
];

export const KSeFIntegrationForm = ({ onSubmit, data }: KSeFIntegrationFormProps) => {
  const methods = useForm<KSeFIntegrationFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: data?.name ?? "",
      environment: data?.environment ?? KSeFEnvironment.Test,
      baseUrl: data?.baseUrl ?? "",
      nip: data?.nip ?? "",
      authType: data?.authType ?? "token",
      authToken: "",
      isEnabled: data?.isEnabled ?? false,
      autoSubmit: data?.autoSubmit ?? false,
      sellerName: data?.sellerName ?? "",
      sellerAddress: data?.sellerAddress ?? "",
      sellerNIP: data?.sellerNIP ?? "",
      sellerBankAccount: data?.sellerBankAccount ?? "",
    },
  });
  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PaziaInput name="name" label="Nazwa" type="text" required />
          <PaziaSelect name="environment" label="Środowisko" options={environmentOptions} required />
          <PaziaInput name="baseUrl" label="Adres URL (BaseUrl)" type="text" required />
          <PaziaInput name="nip" label="NIP" type="text" required />
          <PaziaSelect name="authType" label="Typ autoryzacji" options={authTypeOptions} required />
          <PaziaInput name="authToken" label="Token autoryzacji" type="password" />
        </div>

        <div className="flex flex-col gap-2">
          <PaziaSwitch name="isEnabled" label="Aktywna" />
          <PaziaSwitch name="autoSubmit" label="Automatyczna wysyłka do KSeF" />
        </div>

        <h3 className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">Dane sprzedawcy (opcjonalne)</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PaziaInput name="sellerName" label="Nazwa sprzedawcy" type="text" />
          <PaziaInput name="sellerNIP" label="NIP sprzedawcy" type="text" />
          <PaziaInput name="sellerAddress" label="Adres sprzedawcy" type="text" />
          <PaziaInput name="sellerBankAccount" label="Konto bankowe sprzedawcy" type="text" />
        </div>

        <div className="mt-2 flex justify-end">
          <PaziaFormButton title="Zapisz" type="success" />
        </div>
      </form>
    </FormProvider>
  );
};
